// Libs
import classNames from 'classnames/bind';
import { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseInput, BaseLabel, BaseSelect, Loading } from '~/components/common';
// Others
import { defaultValuesTrailerForm, schema } from './helper';
import { IBaseOption, IBodyUpdate, IResultCallApiFn } from '~/utils/interface/common';
import { commonOptionStatus, EMPTY_STRING, optionRentalStatus, optionTrailerType } from '~/utils/constants/common';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IFormTrailer } from '~/utils/interface/trailer';
import { compareDataUpdate, removeEmptyObjects } from '~/utils/helper/common';
import { createTrailer, getDetailTrailer, updateTrailer } from '~/thunks/trailer/trailerThunk';
// Styles, images, icons
import styles from './TrailerForm.module.scss';
import { icons } from '~/assets';

type Props = {
  idTrailer?: string;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const TrailerFormModal = (props: Props) => {
  //#region Destructuring Props
  const { idTrailer, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/trailer', 'common/base']);
  const dispatch = useAppDispatch();
  const {
    control,
    setValue,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<IFormTrailer>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesTrailerForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [initialData, setInitialData] = useState<IFormTrailer>(defaultValuesTrailerForm);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!idTrailer) return;

    handleGetDetailTrailer(idTrailer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idTrailer]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailTrailer = (idTrailer: string) => {
    setIsLoading(true);

    dispatch(getDetailTrailer(idTrailer))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, ...remainingData } = res?.data;
        const body: IFormTrailer = {
          ...remainingData,
          name: remainingData?.name || EMPTY_STRING,
          trailerType: remainingData?.trailerType || EMPTY_STRING,
          rentalStatus: remainingData?.rentalStatus || EMPTY_STRING,
          status: remainingData?.status || EMPTY_STRING,
          note: remainingData?.note || EMPTY_STRING,
        };

        setInitialData(body);

        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseModal = () => {
    onClose && onClose();
    reset();
  };

  const handleFormTrailer = (data: IFormTrailer) => {
    if (idTrailer) {
      handleEditTrailer(data);
    } else {
      handleAddTrailer(data);
    }

    setIsLoading(true);
  };

  const handleAddTrailer = (data: IFormTrailer) => {
    const convertData = removeEmptyObjects(data) as IFormTrailer;

    dispatch(createTrailer(convertData))
      .unwrap()
      .then((res) => {
        handleCloseModal();
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleEditTrailer = async (data: IFormTrailer) => {
    const dataChange = compareDataUpdate(initialData, data) as IFormTrailer;

    const params: IBodyUpdate<IFormTrailer> = {
      id: idTrailer || EMPTY_STRING,
      body: dataChange,
    };

    dispatch(updateTrailer(params))
      .unwrap()
      .then(() => {
        handleCloseModal();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchOptionsTrailerType = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionTrailerType,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsRentalStatus = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionRentalStatus,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsStatus = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: commonOptionStatus,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchDataTrailerList = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionTrailerType,
      hasMoreResp: false,
    };

    return result;
  }, []);
  //#endregion Handle Function

  return (
    <Dialog
      id='TrailerFormModalComponent'
      aria-labelledby='TrailerFormModal'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle id='trailerFormModal' className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel
              label={t(idTrailer ? 'form_add_trailer_title_edit' : 'form_add_trailer_title')}
              styleType='bigTitle'
            />
          </Stack>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2 columnSpacing={'24px'} rowSpacing={'24px'} container>
          <Grid2 size={6}>
            <Controller
              control={control}
              name={'name'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('form_add_trailer_title_name')}
                    placeholder={t('form_add_trailer_name_placeholder')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.name?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'trailerType'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('form_add_trailer_title_trailer_type')}
                    placeholder={t('form_add_trailer_trailer_placeholder')}
                    callApiFn={fetchOptionsTrailerType}
                    value={value}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.trailerType?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'rentalStatus'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('form_add_trailer_rental_status')}
                    placeholder={t('form_add_trailer_rental_status_placeholder')}
                    callApiFn={fetchOptionsRentalStatus}
                    value={value}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.rentalStatus?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'status'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('form_add_trailer_status')}
                    placeholder={t('form_add_trailer_status_placeholder')}
                    callApiFn={fetchOptionsStatus}
                    value={value}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.status?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name={'note'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    multiline
                    rows={7}
                    height={'auto'}
                    label={t('form_add_trailer_note')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.note?.message}
                  />
                );
              }}
            />
          </Grid2>

          {/* <Grid2 size={6}>
            <Controller
              control={control}
              name={'compatibleEquipment'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseDynamicSelect
                    multiple={true}
                    height={36}
                    label={t('form_add_trailer_compatible_equipment')}
                    callApiFn={fetchDataTrailerList}
                    value={value}
                    onChange={(selected) => {
                      setValue(
                        'compatibleEquipment',
                        selected.optionsSelected.map((opt) => opt.value)
                      );
                      onChange(selected.idSelected);
                    }}
                    errorMessage={errors?.status?.message}
                  />
                );
              }}
            />
          </Grid2> */}
        </Grid2>
      </DialogContent>

      {isLoading && <Loading />}

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button
          variant='contained'
          className={cx('buttonSave')}
          onClick={handleSubmit(handleFormTrailer)}
          disabled={idTrailer ? !isDirty : false}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrailerFormModal;
